import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { AuthContext } from '../contexts/authcontext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PROBLEM_TYPES = [
  { value: 'Bug', label: 'Something is broken', hint: 'A page, button or feature is not working' },
  { value: 'Booking Issue', label: 'Booking or cancelling', hint: 'Trouble reserving, changing or cancelling a session' },
  { value: 'Video Call Issue', label: 'Video call', hint: 'Could not connect, no audio, poor quality' },
  { value: 'Account Issue', label: 'My account', hint: 'Login, password or profile details' },
  { value: 'Therapist Issue', label: 'A session or practitioner', hint: 'Something about a session you attended' },
  { value: 'Feature Request', label: 'A suggestion', hint: 'Something you would like us to add' },
  { value: 'Other', label: 'Something else', hint: '' }
];

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;

const ReportProblem = () => {
  const { user } = useContext(AuthContext);

  const [problemType, setProblemType] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);

    if (picked.length + files.length > MAX_FILES) {
      toast.error(`You can attach up to ${MAX_FILES} files`);
      return;
    }

    const oversized = picked.find(f => f.size > MAX_BYTES);
    if (oversized) {
      toast.error(`"${oversized.name}" is larger than 10MB`);
      return;
    }

    setFiles(prev => [...prev, ...picked]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!problemType) {
      toast.error('Please choose what the problem is about');
      return;
    }
    if (description.trim().length < 10) {
      toast.error('Please describe the issue in a little more detail');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('problemType', problemType);
      formData.append('description', description.trim());
      formData.append('priority', priority);
      if (additionalInfo.trim()) formData.append('additionalInfo', additionalInfo.trim());

      // The old version appended a single `attachment` even when null, which
      // sent the literal string "null", and showed a multi-file list while only
      // ever submitting the first one.
      files.forEach(file => formData.append('attachments', file));

      // Content-Type is deliberately not set. The browser adds it along with the
      // multipart boundary; setting it by hand omits the boundary and the
      // server cannot parse the body.
      const { data } = await api.post('/report-problem', formData);

      setSent(true);
      toast.success(data.message || 'Report sent');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not send your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = PROBLEM_TYPES.find(t => t.value === problemType);

  if (sent) {
    return (
      <div id="uxaah-report">
        <ReportStyles />
        <ToastContainer position="top-center" />
        <div className="uxr-wrap">
          <div className="uxr-card uxr-done">
            <div className="uxr-tick" aria-hidden="true">✓</div>
            <h1 className="uxr-done-title">Report sent</h1>
            <p className="uxr-body">
              Thanks for telling us. We read every report and will reply to{' '}
              <strong>{user?.email}</strong> once we have looked into it.
            </p>
            <div className="uxr-done-actions">
              <button
                onClick={() => {
                  setSent(false);
                  setProblemType('');
                  setDescription('');
                  setAdditionalInfo('');
                  setPriority('Medium');
                  setFiles([]);
                }}
                className="uxr-btn uxr-btn--outline"
              >
                Report something else
              </button>
              <Link to="/home" className="uxr-btn uxr-btn--primary">Back to home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="uxaah-report">
      <ReportStyles />
      <ToastContainer position="top-center" />

      <div className="uxr-wrap">
        <header className="uxr-head">
          <span className="uxr-eyebrow">Support</span>
          <h1 className="uxr-title">Report a problem</h1>
          <p className="uxr-lede">
            Tell us what went wrong and we will look into it. The more specific you
            can be, the faster we can fix it.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="uxr-card">

          {/* ── Problem type ── */}
          <fieldset className="uxr-fieldset">
            <legend className="uxr-legend">What is this about?</legend>
            <div className="uxr-types">
              {PROBLEM_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setProblemType(type.value)}
                  className={`uxr-type ${problemType === type.value ? 'is-active' : ''}`}
                  disabled={submitting}
                >
                  <span className="uxr-type-label">{type.label}</span>
                  {type.hint && <span className="uxr-type-hint">{type.hint}</span>}
                </button>
              ))}
            </div>
          </fieldset>

          {/* ── Description ── */}
          <label className="uxr-label" htmlFor="uxr-desc">
            What happened?
          </label>
          <textarea
            id="uxr-desc"
            className="uxr-input uxr-textarea"
            placeholder={
              selectedType?.value === 'Video Call Issue'
                ? 'For example: I clicked accept and the screen stayed on "Connecting" for a minute, then nothing happened.'
                : 'Describe what you were doing and what went wrong.'
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            rows={5}
          />
          <span className="uxr-hint">
            {description.trim().length < 10
              ? 'A sentence or two is plenty.'
              : `${description.trim().length} characters`}
          </span>

          {/* ── Priority ── */}
          <label className="uxr-label">How much is this affecting you?</label>
          <div className="uxr-priority">
            {PRIORITIES.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                className={`uxr-chip ${priority === p ? 'is-active' : ''}`}
                disabled={submitting}
              >
                {p}
              </button>
            ))}
          </div>

          {/* ── Attachments ── */}
          <label className="uxr-label" htmlFor="uxr-files">
            Screenshots <span className="uxr-optional">optional</span>
          </label>

          <label htmlFor="uxr-files" className="uxr-drop">
            <span className="uxr-drop-icon" aria-hidden="true">📎</span>
            <span className="uxr-drop-text">Choose images or a PDF</span>
            <span className="uxr-drop-sub">Up to {MAX_FILES} files, 10MB each</span>
            <input
              id="uxr-files"
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={handleFileChange}
              disabled={submitting}
              hidden
            />
          </label>

          {files.length > 0 && (
            <ul className="uxr-files">
              {files.map((file, i) => (
                <li key={`${file.name}-${i}`} className="uxr-file">
                  <span className="uxr-file-name">{file.name}</span>
                  <span className="uxr-file-size">
                    {(file.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="uxr-file-remove"
                    aria-label={`Remove ${file.name}`}
                    disabled={submitting}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* ── Extra ── */}
          <label className="uxr-label" htmlFor="uxr-extra">
            Anything else <span className="uxr-optional">optional</span>
          </label>
          <textarea
            id="uxr-extra"
            className="uxr-input uxr-textarea uxr-textarea--short"
            placeholder="Device, browser, or anything else that might help."
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            disabled={submitting}
            rows={3}
          />

          {/* Email is no longer a form field — the server reads it from your
              account, so a report cannot claim to be from someone else. */}
          <p className="uxr-reply">
            We will reply to <strong>{user?.email}</strong>.
          </p>

          <button
            type="submit"
            className="uxr-btn uxr-btn--primary uxr-btn--block"
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Send report'}
          </button>
        </form>

        <p className="uxr-crisis">
          If you are in crisis or need urgent help, please call{' '}
          <a href="tel:14416">Tele-MANAS on 14416</a> — free, 24 hours.
        </p>
      </div>
    </div>
  );
};

/* Scoped under #uxaah-report so global rules in style.css cannot bleed in. */
const ReportStyles = () => (
  <style>{`
    #uxaah-report {
      --uxr-navy:      #1a2344;
      --uxr-blue:      #303c6c;
      --uxr-orange:    #e8622a;
      --uxr-sage:      #4a7c6f;
      --uxr-sage-lite: #e8f2ef;
      --uxr-clay:      #c0392b;
      --uxr-ink:       #111827;
      --uxr-body:      #374151;
      --uxr-muted:     #6b7280;
      --uxr-line:      rgba(48,60,108,0.10);

      font-family: 'DM Sans', system-ui, sans-serif;
      background: #f9fafc;
      color: var(--uxr-ink);
      min-height: 100vh;
      padding: 48px 0 80px;
    }
    #uxaah-report *, #uxaah-report *::before, #uxaah-report *::after { box-sizing: border-box; }

    #uxaah-report .uxr-wrap { max-width: 680px; margin: 0 auto; padding: 0 24px; }

    #uxaah-report .uxr-head { text-align: center; margin-bottom: 32px; }
    #uxaah-report .uxr-eyebrow {
      display: block; font-size: 11.5px; font-weight: 700;
      letter-spacing: 0.16em; text-transform: uppercase;
      color: var(--uxr-blue); margin-bottom: 10px;
    }
    #uxaah-report .uxr-title {
      font-family: 'DM Serif Display', Georgia, serif;
      font-size: clamp(30px, 5vw, 42px); color: var(--uxr-navy);
      margin: 0 0 12px; font-weight: 400; letter-spacing: -0.01em;
    }
    #uxaah-report .uxr-lede {
      font-size: 16px; line-height: 1.65; color: var(--uxr-muted);
      max-width: 480px; margin: 0 auto;
    }

    #uxaah-report .uxr-card {
      background: #ffffff; border-radius: 24px; padding: 36px;
      border: 1px solid rgba(48,60,108,0.07);
      box-shadow: 0 12px 32px rgba(48,60,108,0.07);
    }

    #uxaah-report .uxr-fieldset { border: none; padding: 0; margin: 0 0 8px; }
    #uxaah-report .uxr-legend {
      font-size: 12.5px; font-weight: 700; letter-spacing: 0.03em;
      color: var(--uxr-body); padding: 0; margin-bottom: 12px;
    }

    #uxaah-report .uxr-types { display: grid; gap: 10px; }
    #uxaah-report .uxr-type {
      display: flex; flex-direction: column; gap: 3px;
      text-align: left; padding: 14px 18px; border-radius: 14px;
      border: 1.5px solid var(--uxr-line); background: #ffffff;
      font-family: inherit; cursor: pointer;
      transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
    }
    #uxaah-report .uxr-type:hover:not(:disabled) {
      border-color: var(--uxr-blue); transform: translateY(-1px);
    }
    #uxaah-report .uxr-type.is-active {
      border-color: var(--uxr-orange); background: rgba(232,98,42,0.05);
    }
    #uxaah-report .uxr-type-label { font-size: 14.5px; font-weight: 600; color: var(--uxr-navy); }
    #uxaah-report .uxr-type-hint { font-size: 12.5px; color: var(--uxr-muted); }

    #uxaah-report .uxr-label {
      display: block; font-size: 12.5px; font-weight: 700;
      letter-spacing: 0.03em; color: var(--uxr-body); margin: 26px 0 8px;
    }
    #uxaah-report .uxr-optional {
      font-weight: 500; text-transform: none; letter-spacing: 0;
      color: var(--uxr-muted); font-size: 12px;
    }

    #uxaah-report .uxr-input {
      width: 100%; padding: 13px 16px; border-radius: 12px;
      border: 1.5px solid var(--uxr-line);
      font-family: inherit; font-size: 14.5px; color: var(--uxr-ink);
      background: #ffffff; outline: none;
      transition: border-color 0.25s ease, box-shadow 0.25s ease;
    }
    #uxaah-report .uxr-input::placeholder { color: #b6bcc7; }
    #uxaah-report .uxr-input:focus {
      border-color: var(--uxr-blue); box-shadow: 0 0 0 4px rgba(48,60,108,0.09);
    }
    #uxaah-report .uxr-textarea { resize: vertical; min-height: 120px; line-height: 1.6; }
    #uxaah-report .uxr-textarea--short { min-height: 76px; }

    #uxaah-report .uxr-hint {
      display: block; font-size: 12px; color: var(--uxr-muted); margin-top: 7px;
    }

    #uxaah-report .uxr-priority { display: flex; flex-wrap: wrap; gap: 8px; }
    #uxaah-report .uxr-chip {
      padding: 9px 20px; border-radius: 50px;
      border: 1.5px solid var(--uxr-line); background: #ffffff;
      font-family: inherit; font-size: 13.5px; font-weight: 600;
      color: var(--uxr-body); cursor: pointer;
      transition: all 0.2s ease;
    }
    #uxaah-report .uxr-chip:hover:not(:disabled) { border-color: var(--uxr-blue); color: var(--uxr-blue); }
    #uxaah-report .uxr-chip.is-active {
      background: var(--uxr-navy); border-color: var(--uxr-navy); color: #ffffff;
    }

    #uxaah-report .uxr-drop {
      display: flex; flex-direction: column; align-items: center; gap: 5px;
      padding: 26px; border-radius: 16px; cursor: pointer;
      border: 2px dashed rgba(48,60,108,0.18); background: #fafbfc;
      transition: border-color 0.2s ease, background 0.2s ease;
    }
    #uxaah-report .uxr-drop:hover { border-color: var(--uxr-orange); background: rgba(232,98,42,0.03); }
    #uxaah-report .uxr-drop-icon { font-size: 24px; }
    #uxaah-report .uxr-drop-text { font-size: 14px; font-weight: 600; color: var(--uxr-body); }
    #uxaah-report .uxr-drop-sub { font-size: 12px; color: var(--uxr-muted); }

    #uxaah-report .uxr-files { list-style: none; padding: 0; margin: 12px 0 0; display: grid; gap: 8px; }
    #uxaah-report .uxr-file {
      display: flex; align-items: center; gap: 12px;
      padding: 11px 14px; border-radius: 11px; background: #f6f7fa;
      font-size: 13.5px;
    }
    #uxaah-report .uxr-file-name {
      flex: 1; min-width: 0; overflow: hidden;
      text-overflow: ellipsis; white-space: nowrap; color: var(--uxr-body);
    }
    #uxaah-report .uxr-file-size { font-size: 12px; color: var(--uxr-muted); flex-shrink: 0; }
    #uxaah-report .uxr-file-remove {
      border: none; background: none; cursor: pointer;
      font-size: 20px; line-height: 1; color: var(--uxr-muted);
      padding: 0 2px; flex-shrink: 0;
    }
    #uxaah-report .uxr-file-remove:hover { color: var(--uxr-clay); }

    #uxaah-report .uxr-reply {
      font-size: 13px; color: var(--uxr-muted);
      margin: 26px 0 0; padding: 13px 16px;
      background: var(--uxr-sage-lite); border-radius: 12px;
    }
    #uxaah-report .uxr-reply strong { color: var(--uxr-sage); }

    #uxaah-report .uxr-btn {
      display: inline-flex; align-items: center; justify-content: center;
      padding: 14px 30px; border-radius: 50px; border: none;
      font-family: inherit; font-size: 15px; font-weight: 600;
      cursor: pointer; text-decoration: none;
      transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
    }
    #uxaah-report .uxr-btn--block { width: 100%; margin-top: 20px; }
    #uxaah-report .uxr-btn--primary {
      background: var(--uxr-navy); color: #ffffff;
      box-shadow: 0 6px 18px rgba(26,35,68,0.22);
    }
    #uxaah-report .uxr-btn--primary:hover:not(:disabled) {
      background: var(--uxr-blue); transform: translateY(-2px);
    }
    #uxaah-report .uxr-btn--outline {
      background: #ffffff; color: var(--uxr-body);
      border: 1.5px solid var(--uxr-line);
    }
    #uxaah-report .uxr-btn--outline:hover:not(:disabled) {
      border-color: var(--uxr-blue); color: var(--uxr-blue);
    }
    #uxaah-report .uxr-btn:disabled { opacity: 0.5; cursor: default; transform: none; }

    #uxaah-report .uxr-crisis {
      text-align: center; font-size: 13.5px; color: var(--uxr-muted);
      margin: 24px 0 0; line-height: 1.6;
    }
    #uxaah-report .uxr-crisis a { color: var(--uxr-orange); font-weight: 600; }

    /* ── Sent state ── */
    #uxaah-report .uxr-done { text-align: center; padding: 52px 36px; }
    #uxaah-report .uxr-tick {
      width: 64px; height: 64px; border-radius: 50%; margin: 0 auto 22px;
      display: flex; align-items: center; justify-content: center;
      font-size: 30px; color: #ffffff; background: var(--uxr-sage);
    }
    #uxaah-report .uxr-done-title {
      font-family: 'DM Serif Display', Georgia, serif;
      font-size: 28px; color: var(--uxr-navy); margin: 0 0 12px; font-weight: 400;
    }
    #uxaah-report .uxr-body {
      font-size: 15px; line-height: 1.65; color: var(--uxr-body);
      max-width: 380px; margin: 0 auto 28px;
    }
    #uxaah-report .uxr-done-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

    @media (max-width: 560px) {
      #uxaah-report { padding: 28px 0 60px; }
      #uxaah-report .uxr-wrap { padding: 0 16px; }
      #uxaah-report .uxr-card { padding: 24px; border-radius: 20px; }
      #uxaah-report .uxr-done { padding: 40px 24px; }
      #uxaah-report .uxr-done-actions { flex-direction: column-reverse; }
      #uxaah-report .uxr-btn { width: 100%; }
    }
  `}</style>
);

export default ReportProblem;